import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'

import { Badge, Card } from '../../ui'
import { useReveal } from '../../ui/Cord'
import { dinero, limite } from '../../lib/format'
import { CabeceraMarketing, PieMarketing } from './Landing'

/* ===========================================================================
   Precios. Dos planes y ninguna letra chica: el plan gratis sirve de verdad y
   el Premium es un pago único, porque una boda es un evento con fecha de
   cierre y no una mensualidad.
   =========================================================================== */

const PRECIO_PREMIUM = dinero(3500)

const BOTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors'
const BOTON_SOLIDO = `${BOTON_BASE} bg-tinta text-bruma hover:bg-tinta-suave`
const BOTON_BORDE = `${BOTON_BASE} border border-palo text-tinta hover:bg-palo/30`
const ENLACE_TEXTO = 'text-tinta underline-offset-4 transition-colors hover:text-laton hover:underline'

const INCLUYE_GRATIS: string[] = [
  'Una boda a la vez, con su link propio',
  `Hasta ${limite(50)} invitados`,
  `Hasta ${limite(100)} fotos en la galería`,
  'Confirmaciones desde el celular, sin app ni cuenta',
  'Libro de mensajes con moderación',
  'Guía de planificación completa',
  'Los cinco correos automáticos, editables',
  'Panel compartido para los dos novios',
  'Marca discreta de la plataforma al pie de tu página',
]

const INCLUYE_PREMIUM: string[] = [
  `Invitados: ${limite(null)}`,
  `Fotos: ${limite(null)}`,
  'Tu página sin la marca de la plataforma',
  'Todo lo del plan gratis, sin recortes',
  'Puedes crear otra boda con el plan gratis',
]

const PREGUNTAS: Array<{ pregunta: string; respuesta: ReactNode }> = [
  {
    pregunta: '¿Qué pasa si me paso de 50 invitados?',
    respuesta: (
      <>
        Al guardar el invitado 51 la plataforma te avisa que llegaste al límite del plan gratis y no
        lo agrega. Nada de lo que ya tenías se borra, tu página sigue en línea y tus invitados
        siguen confirmando. Pasas la boda a Premium y ese invitado entra al instante.
      </>
    ),
  },
  {
    pregunta: '¿Puedo pagar después?',
    respuesta: (
      <>
        Sí, esa es la idea. Empieza gratis, arma tu página y tu lista, y decide cuando ya sepas
        cuánta gente va. El pago se hace desde tu panel, en la sección Plan, y se aplica apenas se
        aprueba: los límites desaparecen sin que tengas que rehacer nada ni cambiar de link.
      </>
    ),
  },
  {
    pregunta: '¿Qué pasa con mi página después de la boda?',
    respuesta: (
      <>
        Se queda publicada con el mismo link, para que tus invitados descarguen las fotos y lean el
        libro de mensajes con calma. No hay un segundo cobro ni una fecha de vencimiento. Si algún
        día prefieres cerrarla, la borras tú desde tu panel.
      </>
    ),
  },
  {
    pregunta: '¿Cómo se paga y es una suscripción?',
    respuesta: (
      <>
        No es una suscripción: es un cobro único de <span className="tabular font-mono">{PRECIO_PREMIUM}</span>{' '}
        por esa boda, que no se renueva nunca. Se paga con PayPhone desde tu panel, cuando la boda
        ya existe. Si algún día organizas otra boda, esa otra tiene su propio plan.
      </>
    ),
  },
]

function ListaIncluye({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-tinta/85">
          <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-laton" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

export default function Precios() {
  const planes = useReveal<HTMLDivElement>()
  const preguntas = useReveal<HTMLElement>()

  useEffect(() => {
    document.title = 'Precios — Nuestra Boda'
  }, [])

  return (
    <div className="min-h-dvh bg-bruma">
      <CabeceraMarketing />

      <main className="mx-auto w-full max-w-5xl px-5 sm:px-8">
        <section className="pb-8 pt-10 sm:pt-12">
          <p className="eyebrow">Precios</p>
          <h1 className="mt-3 max-w-[20ch] font-display text-titulo text-tinta">
            Un pago por boda. Nunca una mensualidad.
          </h1>
          <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-tinta/85">
            Una boda es un evento con fecha de cierre, no un servicio que usas todos los meses. Por
            eso el plan Premium se cobra una sola vez, por esa boda, y no se renueva. El plan gratis
            no es una demostración: alcanza para una boda de hasta {limite(50)} invitados.
          </p>
        </section>

        <div ref={planes} className="revelar grid max-w-4xl gap-5 sm:grid-cols-2">
          {/* --- Gratis ---------------------------------------------------- */}
          <Card className="flex flex-col">
            <h2 className="font-display text-2xl text-tinta">Gratis</h2>
            <p className="mt-2 flex items-baseline gap-2">
              <span className="tabular font-mono text-3xl text-tinta">{dinero(0)}</span>
              <span className="text-xs text-palo-hondo">para siempre</span>
            </p>
            <p className="mt-3 text-sm leading-relaxed text-palo-hondo">
              Para la mayoría de bodas familiares. Todo funciona igual, con dos topes.
            </p>

            <hr className="regla my-4" />
            <ListaIncluye items={INCLUYE_GRATIS} />

            <div className="mt-auto pt-6">
              <Link to="/plataforma/registro" className={`${BOTON_BORDE} w-full px-5 py-3 text-sm`}>
                Crear mi boda gratis
              </Link>
            </div>
          </Card>

          {/* --- Premium --------------------------------------------------- */}
          <Card className="flex flex-col">
            <div className="flex items-center gap-3">
              <h2 className="font-display text-2xl text-tinta">Premium</h2>
              <Badge tone="laton">Pago único</Badge>
            </div>
            <p className="mt-2 flex items-baseline gap-2">
              <span className="tabular font-mono text-3xl text-tinta">{PRECIO_PREMIUM}</span>
              <span className="text-xs text-palo-hondo">una vez, por esta boda</span>
            </p>
            <p className="mt-3 text-sm leading-relaxed text-palo-hondo">
              Cuando la lista pasa de {limite(50)} nombres o quieres tu página sin marca ajena.
            </p>

            <hr className="regla my-4" />
            <ListaIncluye items={INCLUYE_PREMIUM} />

            <div className="mt-auto pt-6">
              <Link to="/plataforma/registro" className={`${BOTON_SOLIDO} w-full px-5 py-3 text-sm`}>
                Empezar gratis y pasar a Premium
              </Link>
              <p className="mt-3 text-xs leading-relaxed text-palo-hondo">
                Se paga desde tu panel, cuando tu boda ya existe. Así no pagas antes de ver si el
                plan gratis te alcanza.
              </p>
            </div>
          </Card>
        </div>

        <p className="mt-5 max-w-[58ch] border-l border-palo pl-4 text-xs leading-relaxed text-palo-hondo">
          Con el plan gratis puedes tener una boda a la vez. Las bodas que ya pasaste a Premium no
          cuentan contra ese límite: si organizas otra, vuelves a empezar gratis.
        </p>

        {/* --- Preguntas frecuentes ---------------------------------------- */}
        <section ref={preguntas} className="revelar border-t border-palo/60 py-10 sm:py-12 mt-10">
          <p className="eyebrow">Preguntas frecuentes</p>
          <h2 className="mt-3 font-display text-titulo text-tinta">Lo que suelen preguntar</h2>

          <dl className="mt-6 grid gap-x-10 sm:grid-cols-2">
            {PREGUNTAS.map(({ pregunta, respuesta }) => (
              <div key={pregunta} className="border-t border-palo/60 pb-5 pt-5">
                <dt className="font-display text-lg leading-snug text-tinta">{pregunta}</dt>
                <dd className="mt-2.5 max-w-[58ch] text-sm leading-relaxed text-palo-hondo">
                  {respuesta}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* --- Cierre ------------------------------------------------------- */}
        <section className="border-t border-palo/60 py-10 sm:py-12">
          <h2 className="max-w-[24ch] font-display text-titulo text-tinta">
            Empieza gratis. Decide el pago cuando ya tengas la lista.
          </h2>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/plataforma/registro" className={`${BOTON_SOLIDO} px-5 py-3 text-sm`}>
              Crear mi boda gratis
            </Link>
            <Link to="/plataforma" className={ENLACE_TEXTO}>
              Ver qué incluye la plataforma
            </Link>
          </div>
        </section>
      </main>

      <PieMarketing />
    </div>
  )
}
